import { Button } from '@heroui/react'
import { useTheme } from '@/ui/theme'
import { useNavigate } from 'react-router'
import { useAppSelector } from '@/redux/hooks'
import {
  ReturnMethod,
  FraudScore,
  FraudScores,
  FraudGroup,
  FraudGroups,
  RefundPaymentMethods,
  RefundPaymentMethod,
  RefundActions
} from '@/redux/return/return-slice'
import RefundCard from '@/app/refund-methods/v1/refund-card/refund-card'
import RefundCardKYI from '@/app/refund-methods/v1/refund-card/refund-card-kyi'
import ReverCard from '@/ui/components/rever-card'
import FallbackNotFound from '@/app/return-methods/fallback-not-found'
import { useCalculate, useTranslate, usePages, useReturnMethods } from '@/hooks'
import { useEffect } from 'react'
import { LoadingOverlay } from '@/ui/components/loading-overlay'
import ExchangeKYICards from '@/app/refund-methods/v1/exchange-kyi-cards'
import { RefundMethod as RestRefundMethod } from '@itsrever/returns-api-types'
import clsx from 'clsx'
import { HoffOrder } from '@/ui/custom/hoff-order'
import { usePostHog } from '@posthog/react'
import { isWFPClaim } from '@/utils/worry-free-purchase'

export default function RefundMethodPage() {
  const posthog = usePostHog()

  const theme = useTheme()

  const checkoutId = useAppSelector(
    (store) => store.returnsApi.order.response?.checkout_id
  )

  const settings = useAppSelector(
    (store) => store.settingsApi.settings.response
  )

  const isInStore = settings.in_store_settings?.is_enabled

  const refundMethodsResponse = useAppSelector(
    (store) => store.refundMethodsApi.refundMethods.response
  )
  const dynamicAvailableRefundMethods =
    refundMethodsResponse.result?.refund_methods ?? []

  const fraudScore = refundMethodsResponse.result?.fraud_result
    ?.outcome as FraudScore

  const FraudGroup = refundMethodsResponse.result?.fraud_result
    ?.group as FraudGroup

  const { translate } = useTranslate()
  const { pages } = usePages()

  const navigate = useNavigate()
  const address = useAppSelector((store) => store.return.returnAddress)
  const order = useAppSelector(
    (store) => store.returnsApi.order.response?.order
  )
  const { callReturnMethods } = useReturnMethods(onSuccessReturnMethods)
  const isKeepYourItem = useAppSelector((store) => store.return.keepYourItem)

  const ret = useAppSelector((store) => store.return)
  const refundPaymentMethod = ret.refundPaymentMethod
  const returnedItems = ret.returnedItems
  const moreThanOneItem = returnedItems.length > 1

  const { callCalculate, loadingCalculate } = useCalculate()

  const provisionalRefundAmount =
    useAppSelector((store) => store.refundMethodsApi.provisionalRefundAmount) ??
    0

  const hasExchanges = returnedItems.some(
    (item) => item.item.action === RefundActions.ToExchange
  )
  const onlyExchanges = returnedItems.every(
    (item) => item.item.action === RefundActions.ToExchange
  )

  const refundMethods: RestRefundMethod[] = []
  const refundMethodsKYI: RestRefundMethod[] = []
  const isKYIForced = dynamicAvailableRefundMethods.some((method) => {
    return method.keep_your_item?.force === true
  })

  dynamicAvailableRefundMethods.forEach((method) => {
    if (method.keep_your_item?.force !== true) {
      refundMethods.push({ ...method })
    }
    if (method.keep_your_item?.enabled === true) {
      refundMethodsKYI.push({
        ...method,
        keep_your_item: method.keep_your_item ?? {}
      })
    }
  })

  const keepYourItemOffered = dynamicAvailableRefundMethods.some(
    (method) => method.keep_your_item?.enabled === true
  )
  const giftCardOffered = dynamicAvailableRefundMethods.some((method) =>
    isGiftCardOrPromoCode(method)
  )

  function isGiftCardOrPromoCode(method: RestRefundMethod) {
    return (
      method.method === RefundPaymentMethod.GiftCard ||
      method.method === RefundPaymentMethod.PromoCode
    )
  }

  function onSuccessReturnMethods() {
    navigate(pages.ReturnMethod)
  }

  const handleContinue = () => {
    const method = (refundPaymentMethod.method ?? 0) as RefundPaymentMethod
    posthog.capture('Refund Method', {
      RefundPaymentMethod: RefundPaymentMethods[method],
      FraudScore: FraudScores[fraudScore],
      FraudGroup: FraudGroups[FraudGroup],
      checkout_id: checkoutId
    })

    const onlyNotReceived = returnedItems.every(
      (retItem) =>
        retItem.item.product_return_reason == 'NOT_RECEIVED' ||
        retItem.item.product_return_reason == 'PACKAGE_NOT_ARRIVED'
    )

    if (isWFPClaim(order?.line_items ?? [], returnedItems)) {
      callCalculate(
        ReturnMethod.NoReturnMethod,
        hasExchanges ? address : undefined
      )
      return
    }

    if (isInStore) {
      callCalculate(ReturnMethod.InStore)

      return
    }

    if (onlyNotReceived) {
      if (hasExchanges) {
        callCalculate(ReturnMethod.NoReturnMethod, address)
      } else {
        callCalculate(ReturnMethod.NoReturnMethod)
      }
      return
    } else {
      if (isKeepYourItem) {
        if (hasExchanges) {
          callCalculate(ReturnMethod.NoReturnMethod, address)
        } else {
          callCalculate(ReturnMethod.NoReturnMethod)
        }
      } else {
        callReturnMethods(address)
      }
    }
  }

  useEffect(() => {
    posthog.capture('Fraud Score Result', {
      FraudScore: FraudScores[fraudScore],
      FraudGroup: FraudGroups[FraudGroup],
      checkout_id: checkoutId
    })

    posthog.capture('Refund Method Page Loaded', {
      RefundMethodsOffered: refundMethods.map((method) => method.method ?? 0),
      GiftCardOffered: giftCardOffered,
      GiftCardRDVSurplus: refundMethods.some(
        (method) =>
          isGiftCardOrPromoCode(method) &&
          ((method.rdv_surplus?.surplus_amount ?? 0) > 0 ||
            (method.rdv_surplus?.surplus_percentage ?? 0) > 0)
      ),
      GiftCardFreeShipping: refundMethods.some(
        (method) =>
          isGiftCardOrPromoCode(method) &&
          parseFloat(method.shipping_costs?.max?.amount || '0') === 0
      ),
      isKeepYourItem: isKeepYourItem,
      checkout_id: checkoutId
    })
  }, [])

  const logoUrl = settings.branding?.logo_url
  const title = settings.branding?.title
  const backgroundUrl = settings.branding?.background_image_url

  const isMisako = settings.ecommerce_group == 'misako'
  const isHoff = settings.ecommerce_group == 'hoff'

  if (isHoff) {
    return (
      <>
        {loadingCalculate ? <LoadingOverlay loading /> : null}
        <div className="relative mx-auto flex size-full max-w-[1000px] grow flex-col items-center bg-cover bg-center bg-no-repeat px-4 md:px-10">
          <HoffOrder />
          <div className="size-full">
            {keepYourItemOffered ? null : (
              <p
                className="mb-5 mt-4 text-center text-lg font-normal text-neutral-800 md:mt-0"
                dangerouslySetInnerHTML={{
                  __html: translate('refund_page_title')
                }}
              />
            )}
            {keepYourItemOffered && onlyExchanges ? (
              <>
                <p className="mb-3 text-lg font-semibold text-grey-2">
                  {translate('kyi_refund_page_only_exchanges_title')}
                </p>
                <div className="mt-4">
                  <ExchangeKYICards isKYIForced={isKYIForced} />
                </div>
              </>
            ) : (
              <>
                {refundMethodsKYI.length > 0 && (
                  <>
                    <p className="mb-3 text-lg font-semibold text-grey-2">
                      {moreThanOneItem
                        ? 'Keep your items'
                        : translate('kyi_refund_page_kyi_title')}
                    </p>

                    {refundMethodsKYI.map((method, i) => (
                      <RefundCardKYI
                        key={i}
                        method={method}
                        keepYourItem={method.keep_your_item ?? {}}
                        totalRefundPrice={provisionalRefundAmount}
                        selected={
                          refundPaymentMethod.method === method.method &&
                          isKeepYourItem
                        }
                      />
                    ))}
                  </>
                )}
                {refundMethodsKYI.length && refundMethods.length > 0 ? (
                  <p className="mb-5 mt-3 text-lg font-semibold text-grey-2">
                    {moreThanOneItem
                      ? 'Return your products'
                      : translate('kyi_refund_page_other_methods_title')}
                  </p>
                ) : null}
                {refundMethods.length > 0 &&
                  refundMethods.map((method, i) => (
                    <RefundCard
                      key={i}
                      method={method}
                      totalRefundPrice={provisionalRefundAmount}
                      keepYourItemOffered={keepYourItemOffered}
                      giftCardOffered={giftCardOffered}
                      selected={
                        refundPaymentMethod.method === method.method &&
                        !isKeepYourItem
                      }
                      rdvSurplus={method.rdv_surplus}
                      shippingCosts={method.shipping_costs}
                      serviceFees={method.service_fee_candidates?.[0]}
                    />
                  ))}
                {refundMethods.length === 0 &&
                  refundMethodsKYI.length === 0 && (
                    <FallbackNotFound title="refund_page_no_methods_available" />
                  )}
              </>
            )}

            <div className="mt-2 flex w-full justify-center">
              <button
                id="refundMethodContinue"
                onClick={handleContinue}
                disabled={
                  refundPaymentMethod.method === 0 ||
                  (refundMethods.length === 0 && refundMethodsKYI.length === 0)
                }
                className="ml-auto mt-8 w-fit min-w-44 cursor-pointer rounded-xl bg-black p-4 text-sm text-white disabled:cursor-not-allowed disabled:bg-neutral-500"
              >
                {translate('refund_page_button_continue')}
              </button>
            </div>
          </div>
        </div>
      </>
    )
  }

  if (isMisako) {
    return (
      <>
        {loadingCalculate ? <LoadingOverlay loading /> : null}
        <div className="relative grid grid-cols-1 md:grid-cols-2">
          <div
            className={clsx(
              'sticky top-0 -z-10 h-screen w-full [grid-area:1/1] md:z-10 md:[grid-area:1/2]',
              {
                'bg-neutral-200': !backgroundUrl
              }
            )}
          >
            {backgroundUrl ? (
              <img
                src={backgroundUrl}
                className="size-full object-cover"
                alt=""
              />
            ) : null}
          </div>
          <div className="flex h-full flex-col gap-4 bg-white p-4 [grid-area:1/1] md:[grid-area:1/1] lg:px-10 lg:py-6">
            <div className="flex w-full items-end">
              {logoUrl ? (
                <div className="mr-auto">
                  <img
                    style={
                      {
                        '--logo-desktop-height': '1.5rem',
                        '--logo-mobile-height': '1.2rem'
                      } as React.CSSProperties
                    }
                    width={285}
                    height={70}
                    src={logoUrl}
                    alt={title}
                    className="h-[var(--logo-mobile-height)] w-auto object-contain lg:h-[var(--logo-desktop-height)]"
                  />
                </div>
              ) : null}
              <div className="flex items-center gap-5">
                <a
                  href="https://www.misako.com/pages/wishlist"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src="https://www.misako.com/cdn/shop/files/favorite.svg?v=1731587455"
                    width={20}
                    height={20}
                  />
                </a>
                <a
                  href="https://www.misako.com/account"
                  target="_blank"
                  rel="noreferrer"
                >
                  <svg
                    width="20"
                    height="21"
                    viewBox="0 0 20 21"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19 20V17.7778C19 15.3222 16.9863 13.3333 14.5 13.3333H5.5C3.01375 13.3333 1 15.3222 1 17.7778V20"
                      stroke="rgb(21, 21, 21)"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>
                    <path
                      d="M10 9C12.2091 9 14 7.20914 14 5C14 2.79086 12.2091 1 10 1C7.79086 1 6 2.79086 6 5C6 7.20914 7.79086 9 10 9Z"
                      stroke="rgb(21, 21, 21)"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>
                  </svg>
                </a>
                <a
                  href="https://www.misako.com/collections/top-ventas"
                  target="_blank"
                  rel="noreferrer"
                >
                  <svg
                    width="19"
                    height="19"
                    viewBox="0 0 19 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.6969 13.9469L18 18.25M15.875 8.6875C15.875 12.7951 12.5451 16.125 8.4375 16.125C4.32988 16.125 1 12.7951 1 8.6875C1 4.57988 4.32988 1.25 8.4375 1.25C12.5451 1.25 15.875 4.57988 15.875 8.6875Z"
                      stroke="rgb(21, 21, 21)"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>
                  </svg>
                </a>
              </div>
            </div>
            <div>
              {keepYourItemOffered ? null : (
                <h1
                  className="mb-9 mt-5 text-lg font-[300] tracking-wider text-neutral-600 lg:text-xl"
                  dangerouslySetInnerHTML={{
                    __html: translate('refund_page_title')
                  }}
                />
              )}
              <div className="flex w-full flex-1 flex-col gap-4">
                {keepYourItemOffered && onlyExchanges ? (
                  <>
                    <p className="mb-3 text-lg font-semibold text-grey-2">
                      {translate('kyi_refund_page_only_exchanges_title')}
                    </p>
                    <div className="mt-4">
                      <ExchangeKYICards isKYIForced={isKYIForced} />
                    </div>
                  </>
                ) : (
                  <>
                    {refundMethodsKYI.length > 0 && (
                      <>
                        <p className="mb-3 text-lg font-semibold text-grey-2">
                          {moreThanOneItem
                            ? 'Keep your items'
                            : translate('kyi_refund_page_kyi_title')}
                        </p>

                        {refundMethodsKYI.map((method, i) => (
                          <RefundCardKYI
                            key={i}
                            method={method}
                            keepYourItem={method.keep_your_item ?? {}}
                            totalRefundPrice={provisionalRefundAmount}
                            selected={
                              refundPaymentMethod.method === method.method &&
                              isKeepYourItem
                            }
                          />
                        ))}
                      </>
                    )}
                    {refundMethodsKYI.length && refundMethods.length > 0 ? (
                      <p className="mb-5 mt-3 text-lg font-semibold text-grey-2">
                        {moreThanOneItem
                          ? 'Return your products'
                          : translate('kyi_refund_page_other_methods_title')}
                      </p>
                    ) : null}
                    {refundMethods.length > 0 &&
                      refundMethods.map((method, i) => (
                        <RefundCard
                          key={i}
                          method={method}
                          totalRefundPrice={provisionalRefundAmount}
                          keepYourItemOffered={keepYourItemOffered}
                          giftCardOffered={giftCardOffered}
                          selected={
                            refundPaymentMethod.method === method.method &&
                            !isKeepYourItem
                          }
                          rdvSurplus={method.rdv_surplus}
                          shippingCosts={method.shipping_costs}
                          serviceFees={method.service_fee_candidates?.[0]}
                        />
                      ))}
                    {refundMethods.length === 0 &&
                      refundMethodsKYI.length === 0 && (
                        <FallbackNotFound title="refund_page_no_methods_available" />
                      )}
                  </>
                )}
              </div>
            </div>
            <div className="mt-auto flex items-center justify-between gap-4">
              <div className="w-full md:max-w-[180px]">
                <Button
                  onPress={() => navigate(-1)}
                  id="returnAddressContinue"
                  fullWidth
                  variant="bordered"
                  radius="sm"
                  style={{
                    borderColor: theme.colors.primary.main,
                    color: theme.colors.primary.main,
                    borderRadius: theme.borderRadius
                  }}
                >
                  <span className="text-[13px] font-[300] uppercase tracking-wide">
                    Volver
                  </span>
                </Button>
              </div>
              <div className="w-full md:max-w-[180px]">
                <Button
                  id="refundMethodContinue"
                  onPress={handleContinue}
                  fullWidth
                  isDisabled={
                    refundPaymentMethod.method === 0 ||
                    (refundMethods.length === 0 &&
                      refundMethodsKYI.length === 0)
                  }
                  variant="solid"
                  radius="sm"
                  style={{
                    backgroundColor: theme.colors.primary.main,
                    color: theme.colors.primary.contrastText,
                    borderRadius: theme.borderRadius
                  }}
                >
                  <span className="text-[13px] font-[300] uppercase tracking-wide">
                    {translate('refund_page_button_continue')}
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      {loadingCalculate ? <LoadingOverlay loading /> : null}
      <div className="relative flex flex-1 flex-col content-center items-center lg:bg-[#f2f2f2]">
        <ReverCard className="!w-full">
          <div className="px-8 md:px-0">
            {keepYourItemOffered ? null : (
              <p
                className="mb-5 text-center text-lg font-normal text-neutral-800"
                dangerouslySetInnerHTML={{
                  __html: translate('refund_page_title')
                }}
              />
            )}
            {keepYourItemOffered && onlyExchanges ? (
              <>
                <p className="mb-3 text-lg font-semibold text-grey-2">
                  {translate('kyi_refund_page_only_exchanges_title')}
                </p>
                <div className="mt-4">
                  <ExchangeKYICards isKYIForced={isKYIForced} />
                </div>
              </>
            ) : (
              <>
                {refundMethodsKYI.length > 0 && (
                  <>
                    <p className="mb-3 text-lg font-semibold text-grey-2">
                      {moreThanOneItem
                        ? 'Keep your items'
                        : translate('kyi_refund_page_kyi_title')}
                    </p>

                    {refundMethodsKYI.map((method, i) => (
                      <RefundCardKYI
                        key={i}
                        method={method}
                        keepYourItem={method.keep_your_item ?? {}}
                        totalRefundPrice={provisionalRefundAmount}
                        selected={
                          refundPaymentMethod.method === method.method &&
                          isKeepYourItem
                        }
                      />
                    ))}
                  </>
                )}
                {refundMethodsKYI.length && refundMethods.length > 0 ? (
                  <p className="mb-5 mt-3 text-lg font-semibold text-grey-2">
                    {moreThanOneItem
                      ? 'Return your products'
                      : translate('kyi_refund_page_other_methods_title')}
                  </p>
                ) : null}
                {refundMethods.length > 0 &&
                  refundMethods.map((method, i) => (
                    <RefundCard
                      key={i}
                      method={method}
                      totalRefundPrice={provisionalRefundAmount}
                      keepYourItemOffered={keepYourItemOffered}
                      giftCardOffered={giftCardOffered}
                      selected={
                        refundPaymentMethod.method === method.method &&
                        !isKeepYourItem
                      }
                      rdvSurplus={method.rdv_surplus}
                      shippingCosts={method.shipping_costs}
                      serviceFees={method.service_fee_candidates?.[0]}
                    />
                  ))}
                {refundMethods.length === 0 &&
                  refundMethodsKYI.length === 0 && (
                    <FallbackNotFound title="refund_page_no_methods_available" />
                  )}
              </>
            )}
          </div>
          <div className="sticky bottom-0 flex w-full justify-center bg-white px-8 py-2.5 md:relative md:p-0">
            <div className="w-full md:max-w-[280px]">
              <Button
                id="refundMethodContinue"
                onPress={handleContinue}
                fullWidth
                size="lg"
                isDisabled={
                  refundPaymentMethod.method === 0 ||
                  (refundMethods.length === 0 && refundMethodsKYI.length === 0)
                }
                variant="solid"
                radius="sm"
                style={{
                  backgroundColor: theme.colors.primary.main,
                  color: theme.colors.primary.contrastText,
                  borderRadius: theme.borderRadius
                }}
              >
                {translate('refund_page_button_continue')}
              </Button>
            </div>
          </div>
        </ReverCard>
      </div>
    </>
  )
}
